// Sends the frontend log outbox (BEAST-14120). Every tab and the SharedWorker run it; a Web Lock makes sure
// only one of them sends at a time, so whoever is alive sends - after a reload the new tab also sends what
// the old tab wrote, after the worker died the tabs carry on. Records are deleted only after a 2xx, so a
// failed send loses nothing. At most one send round per SEND_INTERVAL_MS for the whole browser.
// Limits are based on production volume (2026-10-22): a heavy agent with 3 tabs + worker logs ~0.5-0.65 MB
// per minute (p95 ~1.4 MB, max ~2.7 MB), a typical user ~100 KB.
import {
    appendLogEvents,
    deleteRecords,
    enforceLimits,
    getOutboxMeta,
    isLogOutboxAvailable,
    readSendable,
    toStorableEvent,
    updateOutboxMeta,
} from './logOutbox';
import { BoundedLogBuffer, createLogsDroppedEvent, LOGS_DROPPED_EVENT } from 'utils/logging/boundedLogBuffer';

export const SEND_INTERVAL_MS = 60_000;
export const MAX_BACKOFF_MS = 5 * 60_000;
export const REQUEST_TIMEOUT_MS = 15_000;
// Records are already on disk, so a batch does not need keepalive (and its 64 KB limit); 512 KB stays below
// common proxy body limits (nginx default 1 MB) and keeps a heavy agent at 1-2 requests per minute.
export const MAX_BATCH_BYTES = 512_000;
export const MAX_BATCH_EVENTS = 2000;
// Cap of one round: covers normal operation of the heaviest agents in one round, while a backlog after an
// outage drains over a few minutes instead of in one burst (from every browser of a site at once).
export const MAX_ROUND_BYTES = 2 * 1024 * 1024;
export const MAX_ROUND_REQUESTS = 40;
// The SharedWorker sends in normal operation (no UI, so the send work never lands on a tab's main thread, e.g.
// a video wall). A tab takes over regular sends only when the worker has not shown up for this long (dead,
// or not started yet); forced sends (network back, logout) go from any context.
export const TAB_TAKEOVER_AFTER_MS = 150_000;
export const SENDER_ROLE = { WORKER: 'worker', TAB: 'tab' };
// A forced send (network back) starts after a random delay of up to this, so the browsers of a whole site
// that come back online together do not all send at the same moment.
export const FORCED_SEND_MAX_JITTER_MS = 30_000;
// Every tab and the worker get the online event and schedule a forced send; while the server is still down
// that would be one failed round per context. A forced round started this soon after another one is skipped.
export const FORCED_ROUND_DEDUP_MS = 10_000;
// The in-memory fallback has no byte accounting per record, so it keeps batches small by count.
const FALLBACK_BATCH_EVENTS = 500;
export const LOG_OUTBOX_LOCK = 'aiq-log-outbox';
// Events the server may reject (413) and that get dropped in one round, before the round backs off instead.
export const MAX_REJECTED_PER_ROUND = 10;

export const SEND_RESULT = { OK: 'ok', RETRY: 'retry', REJECTED: 'rejected' };
// A tab writes its first logs before it knows its user and settles their author once /auth answers. Records
// still waiting after this long belong to a tab that died first: they are sent with an unknown author. Generous on
// purpose: a slow /auth must not cost a live tab its author, and only the logs of a dead tab ever wait this long.
export const PENDING_AUTHOR_MAX_AGE_MS = 3 * 60 * 1000;

let senderUrl = null;
let senderRole = SENDER_ROLE.TAB;
let intervalId = null;
let onlineScope = null;
let scheduledForcedSend = null;
let releaseScheduledDelay = null;
let scheduledDelayTimer = null;
let scheduledSkipsDedup = false;

let tabTookOver = false;
let tabStartedAt = 0;
let tabSawWorkerAlive = false;

const backoffMs = (failures) => Math.min(SEND_INTERVAL_MS * 2 ** Math.max(0, failures - 1), MAX_BACKOFF_MS);
const toKB = (bytes) => `${Math.round(bytes / 1024)} KB`;
const describeFailure = ({ status, error }) => (status ? `status=${status}` : `error=${error ?? 'unknown'}`);

// Lines about the log sending itself go through the (wired) console like any other log: they land in the
// outbox and reach Grafana once sending works again, with their original time.
const logSender = (message) => console.warn(`[LogOutbox] ${message}`);

/**
 * Posts one batch of log events of a single source/username in the beaver format LogsController reads.
 * @returns {Promise<{result: string, status?: number, error?: string}>} result is SEND_RESULT.OK on 2xx,
 *     REJECTED on 413 (payload too large), RETRY otherwise; status / error describe a failure for the logs.
 *     400 is RETRY on purpose: the payload is serialized here, so a 400 points at the server or a proxy (a
 *     contract change, a parsing bug) and applies to every request - treating it as a bad event would drop
 *     the whole outbox one event at a time. A 500 is RETRY too: LogsController skips an event it cannot handle
 *     and still answers 2xx, so a 500 means the server, never one event.
 *     The author (username) decides meta:
 *     - a name: meta.originalUsername, stored as username;
 *     - null: meta.authorUnknown, stored as anonymousUser - for outbox records, which can be sent later from
 *       another user's session, so the sending session must never be taken for their author;
 *     - undefined: meta {}, the server takes the user of the sending session - for the in-memory fallback,
 *       which sends a tab's (or the worker's) own logs right away from its own session.
 */
function metaFor(username) {
    if (username) return { originalUsername: username };
    if (username === null) return { authorUnknown: true };
    return {};
}

export async function postLogBatch(url, { source, username, events }) {
    let body;
    try {
        body = JSON.stringify({
            events,
            meta: metaFor(username),
            tracking: [{ uid: source }],
        });
    } catch {
        // Unserializable payload (e.g. circular): a payload problem like 413, so it is dropped with a report
        // instead of throwing after the records were already taken out of a buffer.
        return { result: SEND_RESULT.REJECTED, error: 'unserializable payload' };
    }
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            credentials: 'include',
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
        if (response.ok) return { result: SEND_RESULT.OK, status: response.status };
        if (response.status === 413) return { result: SEND_RESULT.REJECTED, status: response.status };
        return { result: SEND_RESULT.RETRY, status: response.status };
    } catch (err) {
        return { result: SEND_RESULT.RETRY, error: err?.name === 'TimeoutError' ? 'TimeoutError' : `${err?.name}: ${err?.message}` };
    }
}

// Groups the oldest records by source/username (one request each, original order kept within a group) and
// splits every group into batches below the byte / event limits.
function toBatches(records) {
    const groups = new Map();
    for (const record of records) {
        const groupKey = `${record.source}|${record.username}`;
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(record);
    }
    const batches = [];
    for (const group of groups.values()) {
        let batch = [];
        let bytes = 0;
        for (const record of group) {
            if (batch.length && (bytes + record.size > MAX_BATCH_BYTES || batch.length >= MAX_BATCH_EVENTS)) {
                batches.push(batch);
                batch = [];
                bytes = 0;
            }
            batch.push(record);
            bytes += record.size;
        }
        if (batch.length) batches.push(batch);
    }
    return batches;
}

function toConsecutiveRuns(records) {
    const runs = [];
    for (const record of records) {
        const last = runs.at(-1);
        if (last && last[0].source === record.source && last[0].username === record.username) last.push(record);
        else runs.push([record]);
    }
    return runs;
}

// event_id lets analysis spot a record sent twice (a 2xx whose delete did not happen: timeout, reload). It is
// unique only together with username and log_source (the key counts per browser, and every worker's source is
// 'SharedWorker'), so look for duplicates within one user's source, not across users.
const toWireEvent = (record) => ({ ...record.event, id: `${record.source}-${record.key}` });

async function onSent(records, round) {
    await deleteRecords(records);
    round.events += records.length;
    round.bytes += records.reduce((sum, record) => sum + (record.size ?? 0), 0);
}

// Sends a batch; a batch the server rejects as too large is split until the one oversized event is found
// and dropped, so a single unsendable event cannot block the queue. The number of dropped events per round
// is capped (round.rejected), so a server that keeps rejecting cannot empty the outbox or loop forever.
async function sendRecords(url, records, round) {
    const { source, username } = records[0];
    round.requests += 1;
    const response = await postLogBatch(url, { source, username, events: records.map(toWireEvent) });
    if (response.result === SEND_RESULT.OK) {
        await onSent(records, round);
        return response;
    }
    if (response.result === SEND_RESULT.RETRY) return response;

    logSender(
        `Server rejected a batch of ${records.length} log events (${describeFailure(response)}), ${records.length > 1 ? 'splitting it' : 'dropping the event'}`
    );
    if (records.length === 1) {
        if (round.rejected >= MAX_REJECTED_PER_ROUND) return { ...response, result: SEND_RESULT.RETRY };
        round.rejected += 1;
        await deleteRecords(records);
        // A rejected drop report is just removed; reporting it again would create a new one forever.
        if (records[0].event?.event !== LOGS_DROPPED_EVENT) {
            await appendLogEvents({
                source,
                username,
                events: [createLogsDroppedEvent({ count: 1, from: records[0].ts, to: records[0].ts, reason: 'rejected by server' })],
            });
        }
        return { result: SEND_RESULT.OK };
    }
    const middle = Math.ceil(records.length / 2);
    const first = await sendRecords(url, records.slice(0, middle), round);
    if (first.result === SEND_RESULT.RETRY) return first;
    return sendRecords(url, records.slice(middle), round);
}

export async function sendPendingLogs({ force = false, deduplicate = true, url = senderUrl } = {}) {
    const now = Date.now();
    const meta = await getOutboxMeta();
    if (!force && now < meta.nextAttemptAt) return;
    if (force) {
        if (deduplicate && now - (meta.lastForcedAttemptAt ?? 0) < FORCED_ROUND_DEDUP_MS) return;
        await updateOutboxMeta({ lastForcedAttemptAt: now });
    }

    await enforceLimits(now);
    const round = { rejected: 0, requests: 0, events: 0, bytes: 0 };
    const roundIsFull = () => round.bytes >= MAX_ROUND_BYTES || round.requests >= MAX_ROUND_REQUESTS;
    for (;;) {
        if (roundIsFull()) break;
        const records = await readSendable({ limit: MAX_BATCH_EVENTS, now, pendingMaxAgeMs: PENDING_AUTHOR_MAX_AGE_MS });
        if (!records.length) break;
        for (const batch of toBatches(records)) {
            if (roundIsFull()) break;
            const response = await sendRecords(url, batch, round);
            if (response.result === SEND_RESULT.RETRY) {
                // A forced round only skips the wait; it keeps counting failures, so the backoff keeps growing.
                const failures = meta.failures + 1;
                const firstFailureAt = meta.firstFailureAt || now;
                await updateOutboxMeta({ failures, firstFailureAt, nextAttemptAt: now + backoffMs(failures) });
                logSender(
                    `Sending logs failed (${describeFailure(response)}), attempt=${failures}, next attempt in ${Math.round(backoffMs(failures) / 1000)}s, sender=${senderRole}`
                );
                return;
            }
        }
    }
    if (meta.failures > 0) {
        logSender(
            `Sending logs recovered after ${meta.failures} failed attempts (first failure ${new Date(meta.firstFailureAt).toISOString()}), sent ${round.events} events / ${toKB(round.bytes)} this round`
        );
    }
    await updateOutboxMeta({ failures: 0, firstFailureAt: 0, lastSendAt: now, nextAttemptAt: now + SEND_INTERVAL_MS });
}

// The worker marks itself alive on every regular attempt (also while the network is down and rounds fail),
// so the tabs know whether to stay out of regular sends.
async function shouldSkipRegularAttempt() {
    const now = Date.now();
    if (senderRole === SENDER_ROLE.WORKER) {
        await updateOutboxMeta({ workerSeenAt: now });
        return false;
    }
    const { workerSeenAt = 0 } = await getOutboxMeta();
    const workerAlive = now - workerSeenAt < TAB_TAKEOVER_AFTER_MS;
    if (workerAlive) {
        tabSawWorkerAlive = true;
        if (tabTookOver) {
            logSender('SharedWorker is sending logs again, this tab stops');
            tabTookOver = false;
        }
        return true;
    }
    // workerSeenAt survives browser restarts, so a stale value right after page load only means the worker has
    // not started yet (the tab logger starts first). Give it the same grace period before taking over.
    if (!tabSawWorkerAlive && now - tabStartedAt < TAB_TAKEOVER_AFTER_MS) return true;
    if (!tabTookOver) {
        logSender(
            tabSawWorkerAlive
                ? `SharedWorker not seen for ${Math.round((now - workerSeenAt) / 1000)}s, this tab takes over sending logs`
                : `SharedWorker has not sent logs since this tab started ${Math.round((now - tabStartedAt) / 1000)}s ago, this tab sends them`
        );
        tabTookOver = true;
    }
    return false;
}

/**
 * One send attempt as the interval runs it (forced: true skips waiting for the next attempt time, like a
 * network-back trigger without delay). Exported for tests.
 */
export function attemptLogOutboxSend(options) {
    return attemptSend(options);
}

async function attemptSend(options) {
    if (!senderUrl || !(await isLogOutboxAvailable())) return;
    if (!options?.force) {
        try {
            if (await shouldSkipRegularAttempt()) return;
        } catch {
            // Without the meta record, fall through and try to send.
        }
    }
    const run = () => sendPendingLogs(options);
    try {
        if (!globalThis.navigator?.locks) {
            await run();
            return;
        }
        // A regular attempt skips when another context is sending. A forced one (network is back) waits for
        // the lock instead, otherwise it would be lost whenever it coincides with a regular round.
        await globalThis.navigator.locks.request(LOG_OUTBOX_LOCK, { ifAvailable: !options?.force }, (lock) => (lock ? run() : undefined));
    } catch {
        // Next interval tries again.
    }
}

/**
 * Network is back (online event, STOMP reconnected) or logout: send without waiting out the backoff, after a
 * short random delay. Repeated triggers while one is scheduled share it; a trigger without delay (logout)
 * cuts the delay of an already scheduled one, so it is never held back by an earlier random delay.
 * @param {Object} [options]
 * @param {number} [options.jitterMs] - Delay before sending; random up to FORCED_SEND_MAX_JITTER_MS by default
 * @param {boolean} [options.deduplicate] - false sends even if another context just did a forced round (logout:
 *     these are the last logs of the session)
 */
export function triggerLogOutboxSend({ jitterMs = Math.random() * FORCED_SEND_MAX_JITTER_MS, deduplicate = true } = {}) {
    if (!deduplicate) scheduledSkipsDedup = true;
    if (!scheduledForcedSend) {
        scheduledForcedSend = new Promise((resolve) => {
            releaseScheduledDelay = resolve;
            scheduledDelayTimer = setTimeout(resolve, jitterMs);
        })
            .then(() => {
                const skipDedup = scheduledSkipsDedup;
                scheduledForcedSend = null;
                releaseScheduledDelay = null;
                scheduledSkipsDedup = false;
                return attemptSend({ force: true, deduplicate: !skipDedup });
            })
            .catch(() => {
                scheduledForcedSend = null;
            });
    } else if (jitterMs === 0) {
        clearTimeout(scheduledDelayTimer);
        releaseScheduledDelay?.();
    }
    return scheduledForcedSend;
}

const onOnline = () => triggerLogOutboxSend();

/**
 * @param {Object} options
 * @param {string} options.url - The /logs endpoint
 * @param {Object} [options.globalScope] - window or the worker's self, for the online event
 * @param {string} [options.role] - SENDER_ROLE.WORKER sends every round; SENDER_ROLE.TAB only when the worker
 *     is not around (see TAB_TAKEOVER_AFTER_MS)
 * @returns {Promise<void>} The first send attempt (callers do not need to wait for it)
 */
export function startLogOutboxSender({ url, globalScope = globalThis, role = SENDER_ROLE.TAB }) {
    if (senderUrl) return Promise.resolve();
    senderUrl = url;
    senderRole = role;
    tabStartedAt = Date.now();
    intervalId = setInterval(() => attemptSend(), SEND_INTERVAL_MS);
    onlineScope = globalScope;
    onlineScope?.addEventListener?.('online', onOnline);
    return attemptSend();
}

/**
 * Fallback for a context where the IndexedDB outbox is not available: keeps a capped buffer in memory and
 * sends it at most once per SEND_INTERVAL_MS, putting a failed batch back.
 */
export function createMemoryLogSender({ url, maxRecords = 5000 }) {
    const buffer = new BoundedLogBuffer({ maxRecords });
    let nextAttemptAt = 0;
    let failures = 0;
    let firstFailureAt = 0;
    let inFlight = null;

    // One send at a time: the transport calls this every few seconds, and a slow request (up to the timeout)
    // must not overlap the next one, or both would take from the buffer and put batches back out of order.
    function flush(options) {
        if (!inFlight) inFlight = send(options).finally(() => (inFlight = null));
        return inFlight;
    }

    async function send({ force = false } = {}) {
        const now = Date.now();
        if (!force && now < nextAttemptAt) return;
        let rejectedThisRound = 0;
        while (buffer.size) {
            // One request per run of the same source/username (a worker batch can span a user change). A run the
            // server rejects as too large is split in halves until the oversized event is found, like the outbox.
            const pending = toConsecutiveRuns(buffer.take(FALLBACK_BATCH_EVENTS));
            while (pending.length) {
                const run = pending.shift();
                const { source, username } = run[0];
                const response = await postLogBatch(url, { source, username, events: run.map((record) => record.event) });
                let { result } = response;
                if (result === SEND_RESULT.REJECTED && run.length > 1) {
                    const middle = Math.ceil(run.length / 2);
                    pending.unshift(run.slice(0, middle), run.slice(middle));
                    continue;
                }
                if (result === SEND_RESULT.REJECTED && rejectedThisRound >= MAX_REJECTED_PER_ROUND) result = SEND_RESULT.RETRY;
                if (result === SEND_RESULT.RETRY) {
                    buffer.requeue([...run, ...pending.flat()]);
                    failures += 1;
                    firstFailureAt = firstFailureAt || now;
                    nextAttemptAt = now + backoffMs(failures);
                    logSender(
                        `Sending logs from memory failed (${describeFailure(response)}), attempt=${failures}, next attempt in ${Math.round(backoffMs(failures) / 1000)}s`
                    );
                    return;
                }
                // A single event rejected as too large: dropped like in the outbox, but reported, never silently.
                if (result === SEND_RESULT.REJECTED) rejectedThisRound += 1;
                const rejected = run.filter((record) => record.event?.event !== LOGS_DROPPED_EVENT);
                if (result === SEND_RESULT.REJECTED && rejected.length) {
                    buffer.push({
                        source,
                        username,
                        ts: now,
                        event: createLogsDroppedEvent({
                            count: rejected.length,
                            from: rejected[0].ts,
                            to: rejected.at(-1).ts,
                            reason: 'rejected by server',
                            now,
                        }),
                    });
                }
            }
        }
        if (failures > 0) {
            logSender(
                `Sending logs from memory recovered after ${failures} failed attempts (first failure ${new Date(firstFailureAt).toISOString()})`
            );
        }
        failures = 0;
        firstFailureAt = 0;
        nextAttemptAt = now + SEND_INTERVAL_MS;
    }

    return {
        push(source, username, events) {
            // Same storable form as the outbox (raw console arguments can be circular or hold functions), so one bad
            // event cannot make the whole batch unsendable. No author yet is kept as undefined: this buffer is sent
            // right away from this context's own session, so the server's session user is the right author (see
            // postLogBatch), not anonymousUser.
            const records = events.map((event) => ({
                source,
                username: username || undefined,
                ts: Number(event?.payload?.timestamp) || Date.now(),
                event: toStorableEvent(event).event,
            }));
            buffer.push(...records);
        },
        flush,
        get size() {
            return buffer.size;
        },
    };
}

// Test-only: module state survives between tests otherwise.
export function resetLogOutboxSenderForTests() {
    clearInterval(intervalId);
    onlineScope?.removeEventListener?.('online', onOnline);
    senderUrl = null;
    senderRole = SENDER_ROLE.TAB;
    clearTimeout(scheduledDelayTimer);
    scheduledForcedSend = null;
    releaseScheduledDelay = null;
    scheduledDelayTimer = null;
    scheduledSkipsDedup = false;
    tabTookOver = false;
    tabStartedAt = 0;
    tabSawWorkerAlive = false;
    intervalId = null;
    onlineScope = null;
}
